import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { Suspense, useEffect } from "react";
import { Layout } from "#/components/shell/Layout";
import { SalesIq } from "#/components/shell/SalesIq";
import { captureAttribution } from "#/lib/attribution";
import appCss from "#/styles.css?url";

export interface StorefrontRouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<StorefrontRouterContext>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Learning Plus" },
      {
        name: "description",
        content:
          "We are dedicated to supporting homeschool families with a curated selection of high-quality materials that inspire learning and creativity.",
      },
      {
        "http-equiv": "Content-Security-Policy",
       content: [
          "default-src 'self'",
          "script-src 'self' 'unsafe-inline' https://salesiq.zoho.com https://salesiq.zohopublic.com https://js.zohostatic.com https://js.zohocdn.com https://static.zohocdn.com",
          "style-src 'self' 'unsafe-inline' https://css.zohostatic.com https://css.zohocdn.com static.zohocdn.com",
          "img-src 'self' data: blob: https:",
          "font-src 'self' data: https://fonts.zohostatic.com https://static.zohocdn.com https://css.zohocdn.com",
           "media-src 'self' https://static.zohocdn.com",
          `connect-src 'self' https://api.paymongo.com${
            import.meta.env.DEV ? ` ${import.meta.env.MEDUSA_BACKEND_URL ?? ""}` : ""
          } https://*.zohopublic.com wss://*.zohopublic.com `,
          "frame-src https://salesiq.zoho.com https://salesiq.zohopublic.com",
          "object-src 'none'",
          "base-uri 'self'",
        ].join("; "),
      },
    ],
    links: [
      { rel: 'icon', href: '/favicon/favicon.ico', sizes: '48x48' },
      { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon/favicon-32x32.png' },
      { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicon/favicon-16x16.png' },
      { rel: 'apple-touch-icon', sizes: '180x180', href: '/favicon/apple-touch-icon.png' },
      { rel: 'manifest', href: '/favicon/site.webmanifest' },
      { rel: "stylesheet", href: appCss }
    ]
  }),
  errorComponent: RootErrorFallback,
  shellComponent: RootDocument,
});

function RootDocument({ children }: { children: React.ReactNode }) {
  // Mounted once per document load, which is exactly when a campaign link
  // arrives — an in-app navigation never carries utm_* params. First touch
  // wins, so re-running on a later load is a no-op.
  useEffect(() => {
    captureAttribution();
  }, []);

  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <Layout>
          <Suspense fallback={<RouteFallback />}>{children}</Suspense>
        </Layout>
        <SalesIq />
        <Scripts />
      </body>
    </html>
  );
}

function RouteFallback() {
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      className="flex min-h-[40vh] items-center justify-center px-6 py-24 font-display text-lp-text-lt text-sm"
    >
      Loading…
    </div>
  );
}

function RootErrorFallback({ error }: { error: Error }) {
  return (
    <div className="mx-auto max-w-[640px] px-6 py-24 text-center">
      <h1 className="m-0 font-bold font-display text-2xl text-lp-text-dk">
        Something went wrong
      </h1>
      <p className="mt-4 font-display text-lp-text-lt text-sm">
        {error.message || "An unexpected error occurred."}
      </p>
    </div>
  );
}
